import React, { JSX } from 'react'
import { AnalyticsMetafield, UTMReferrerTotals } from 'types/api'
import { cleanVariables, graphqlRequest } from 'lib/helpers'
import { RefreshingBlock } from './Loading'
import getUTMReferrers from '../graphql/getUTMReferrers.graphql'
import { useQuery } from '@tanstack/react-query'
import { Leaderboard } from './Leaderboard'

export interface UTMLeaderboardProps {
  filters: {
    productOption?: AnalyticsMetafield[]
    bundleOption?: AnalyticsMetafield[]
    range: {
      startDate: Date
      endDate: Date
    }
    project?: AnalyticsMetafield
  }
  token?: string
  client: AnalyticsMetafield | null
}

export const UTMLeaderBoard = ({ token, client, filters }: UTMLeaderboardProps): JSX.Element => {
  const {
    data,
    isFetching
  } = useQuery({
    queryKey: ['dashboard', 'utmLeaderboard', filters, client?.id],

    queryFn: async () => {
      if (token == null) return

      const cleanVar = cleanVariables({
        clientId: client?.id,
        projectId: filters.project?.id,
        productIds: filters?.productOption?.map(p => p.id),
        startDate: filters.range.startDate,
        endDate: filters.range.endDate
      })

      const { utmReferrers } = await graphqlRequest<{ utmReferrers: UTMReferrerTotals }>(
        getUTMReferrers, cleanVar, token
      )

      return { utmReferrers }
    },

    enabled: token != null && client != null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  return (
    <div className='flex flex-col min-h-[280px] gap-4'>
      <div className='relative flex-1 shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-2xl overflow-hidden p-4'>
        {isFetching && RefreshingBlock}
        <Leaderboard
          noDataMessage='No UTM Campaign data for specified filters'
          hoverData={false}
          idField='utmCampaign'
          data={data?.utmReferrers.byCampaign ?? []}
          defaultSortKey='sales'
          columns={['utmCampaign', 'sales']}
          title='Top UTM campaigns by sales'
          limit={4}
        />
      </div>

      <div className='relative flex-1 shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-2xl overflow-hidden p-4'>
        {isFetching && RefreshingBlock}
        <Leaderboard
          noDataMessage='No UTM Medium data for specified filters'
          hoverData={false}
          idField='utmMedium'
          data={data?.utmReferrers.byMedium ?? []}
          defaultSortKey='sales'
          columns={['utmMedium', 'sales']}
          title='Top UTM mediums by sales'
          limit={4}
        />
      </div>
    </div>
  )
}
